import { buyerConditionDisplayName } from '../buyerFacing/conditionDisplayNames';
import {
  canonicalConditionFamily,
  isCoreConditionFamily,
  presentCustomerPatternWindowCopy,
  presentCustomerResultDirection,
  presentPatternAlertReason,
  presentPostControlPattern,
  resolveCanonicalControl,
} from '../continuousSafety/conditionLifecyclePresentation';
import { buildLifecycleScanVerifyPath } from '../continuousSafety/conditionLifecycleNavigation';
import type { ConditionLifecycleSummary } from '../continuousSafety/siteConditionState';
import type { VisualTimeSemantics } from '../continuousSafety/visualLifecycleModel';
import type { OverviewConditionRow, OverviewPeriodConditionRow } from '../continuousSafety/overviewSiteConditionSummary';
import type { CanonicalReportLifecycle } from '../safetyScanReport/canonicalSafetyScanReportPresenter';
import type { SpatialOperationsMode } from './spatialOperationsUrl';

export const NO_AUTHORITATIVE_SPATIAL_GEOMETRY =
  'No authoritative spatial geometry is available for this area.';

export const SPATIAL_OPERATIONS_TITLE = 'Spatial Operations';
export const SPATIAL_OPERATIONS_SUBLINE = 'Find the risk. Fix the condition. Verify the change.';
export const SPATIAL_OPERATIONS_ENRICHMENT_LOADING = 'Loading condition details…';
export const SPATIAL_OPERATIONS_GEOMETRY_LOADING = 'Loading spatial geometry…';
export const SPATIAL_OPERATIONS_HISTORY_LOADING = 'Loading History…';
export const SPATIAL_OPERATIONS_SCAN_LOADING = 'Loading Safety Scan…';

export type SpatialOperationsCameraChoice = {
  cameraId: string;
  deviceId: string | null;
  areaId: string | null;
  areaLabel: string;
  cameraLabel: string;
  mountLabel: string | null;
};

export type SpatialOperationsConditionChoice = {
  conditionId: string;
  family: string;
  label: string;
  cameraId: string | null;
  zoneId: string | null;
  incidentType: string | null;
};

export type SpatialOperationsStageId = 'find' | 'alerts' | 'control' | 'since' | 'verify';

export type SpatialOperationsStageView = {
  id: SpatialOperationsStageId;
  label: string;
  state: string;
  detail: string;
  href: string | null;
};

export type SpatialOperationsScanView = {
  linked: boolean;
  reportId: string | null;
  beforeReportId: string | null;
  afterReportId: string | null;
  lifecycle: CanonicalReportLifecycle | null;
  signed: boolean;
  coverageState: string | null;
  finality: string | null;
  resultDirection: string | null;
  resultDirectionLabel: string | null;
  confirmedLabel: string | null;
  usableHoursLabel: string | null;
  openHref: string | null;
  canOpen: boolean;
  emptyCopy: string;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

export function presentStoredPatternCustomerCopy(
  minEvents: number | null | undefined,
  windowSeconds: number | null | undefined,
): string | null {
  return presentCustomerPatternWindowCopy(minEvents, windowSeconds);
}

export function resolveSpatialOperationsSelection(args: {
  cameras: SpatialOperationsCameraChoice[];
  conditions: SpatialOperationsConditionChoice[];
  requestedCameraId?: string | null;
  requestedConditionId?: string | null;
  requestedMode?: SpatialOperationsMode | null;
}): {
  camera: SpatialOperationsCameraChoice | null;
  condition: SpatialOperationsConditionChoice | null;
  mode: SpatialOperationsMode;
  usedFallback: boolean;
} {
  const requestedCameraId = text(args.requestedCameraId);
  const requestedConditionId = text(args.requestedConditionId);
  const camera = args.cameras.find((item) => item.cameraId === requestedCameraId)
    || args.cameras[0]
    || null;
  const cameraConditions = args.conditions.filter((item) => {
    if (!camera) return false;
    return !item.cameraId || item.cameraId === camera.cameraId;
  });
  const pool = cameraConditions.length ? cameraConditions : args.conditions;
  const ranked = [...pool].sort((a, b) => Number(isCoreConditionFamily(b.family)) - Number(isCoreConditionFamily(a.family)));
  const condition = ranked.find((item) => item.conditionId === requestedConditionId)
    || ranked[0]
    || null;
  const usedFallback = Boolean(
    (requestedCameraId && camera?.cameraId !== requestedCameraId)
    || (requestedConditionId && condition?.conditionId !== requestedConditionId),
  );
  return {
    camera,
    condition,
    mode: args.requestedMode || 'camera',
    usedFallback,
  };
}

export function spatialOperationsIdentityChoices(args: {
  conditions: Array<{
    conditionId: string;
    conditionFamily?: string | null;
    canonicalScope?: { cameraIds?: string[]; zoneIds?: string[]; deviceIds?: string[]; areaId?: string | null } | null;
    operationalScope?: { cameraId?: string | null; deviceId?: string | null; areaId?: string | null } | null;
  }>;
  cameras?: Array<{ camera_id?: string; name?: string; edge_device_id?: string }>;
  placements?: Record<string, { areaId?: string | null; areaName?: string | null; mountLabel?: string | null }>;
}): {
  cameras: SpatialOperationsCameraChoice[];
  conditions: SpatialOperationsConditionChoice[];
} {
  const cameras: SpatialOperationsCameraChoice[] = [];
  const conditions: SpatialOperationsConditionChoice[] = [];
  const seenCameras = new Set<string>();
  const seenConditions = new Set<string>();
  args.conditions.forEach((condition) => {
    const conditionId = text(condition.conditionId);
    if (!conditionId || seenConditions.has(conditionId)) return;
    const family = canonicalConditionFamily(condition.conditionFamily);
    const cameraId = text(condition.canonicalScope?.cameraIds?.[0] || condition.operationalScope?.cameraId);
    const deviceId = text(condition.canonicalScope?.deviceIds?.[0] || condition.operationalScope?.deviceId) || null;
    const zoneId = text(condition.canonicalScope?.zoneIds?.[0]) || null;
    seenConditions.add(conditionId);
    conditions.push({
      conditionId,
      family,
      label: buyerConditionDisplayName(condition.conditionFamily, condition.conditionFamily) || family || 'Condition',
      cameraId: cameraId || null,
      zoneId,
      incidentType: text(condition.conditionFamily) || null,
    });
    if (!cameraId || seenCameras.has(cameraId)) return;
    seenCameras.add(cameraId);
    const listed = (args.cameras || []).find((item) => text(item.camera_id) === cameraId);
    const placement = args.placements?.[cameraId];
    cameras.push({
      cameraId,
      deviceId: deviceId || text(listed?.edge_device_id) || null,
      areaId: text(condition.canonicalScope?.areaId || condition.operationalScope?.areaId || placement?.areaId) || null,
      areaLabel: text(placement?.areaName) || text(listed?.name) || 'Monitored area',
      cameraLabel: text(listed?.name) || cameraId,
      mountLabel: text(placement?.mountLabel) || null,
    });
  });
  return { cameras, conditions };
}

export function conditionChoiceFromRows(
  current: OverviewConditionRow | null | undefined,
  recurring: OverviewPeriodConditionRow | null | undefined,
): SpatialOperationsConditionChoice | null {
  const row = current || recurring;
  if (!row) return null;
  const family = canonicalConditionFamily(row.conditionFamily || row.incidentType);
  return {
    conditionId: row.conditionId,
    family,
    label: buyerConditionDisplayName(row.label, row.incidentType) || text(row.label) || 'Condition',
    cameraId: text(row.cameraId) || null,
    zoneId: text(row.zoneId) || null,
    incidentType: text(row.incidentType) || null,
  };
}

function currentPostureCopy(current: OverviewConditionRow | null | undefined): string | null {
  if (!current) return null;
  if (current.measurementValue == null && !text(current.supportingActivityLabel) && !text(current.bodyLine)) {
    return 'Current activity is not measured for this window.';
  }
  return text(current.bodyLine)
    || text(current.supportingActivityLabel)
    || text(current.resultLabel)
    || 'Observed in the current window.';
}

function recurrenceHistoryCopy(
  recurrence: VisualTimeSemantics['recurrenceRange'] | null | undefined,
): string {
  if (recurrence?.value == null) {
    return 'Recurrence history is unavailable for this window.';
  }
  return `${recurrence.value} recorded occurrences in recurrence history.`;
}

function occurrenceLinkageCopy(linkedOccurrenceCount: number | null | undefined): string {
  if (linkedOccurrenceCount == null) {
    return 'Occurrence-level lifecycle linkage is unavailable.';
  }
  if (linkedOccurrenceCount === 0) {
    return 'Occurrence-level lifecycle links are not available for this recorded history.';
  }
  return `${linkedOccurrenceCount} occurrence-level lifecycle link${linkedOccurrenceCount === 1 ? '' : 's'} available.`;
}

export function presentSpatialOperationsStages(args: {
  conditionLabel: string;
  areaLabel: string;
  zoneLabel?: string | null;
  current?: OverviewConditionRow | null;
  recurring?: OverviewPeriodConditionRow | null;
  recurrenceRange?: VisualTimeSemantics['recurrenceRange'] | null;
  linkedOccurrenceCount?: number | null;
  lifecycle?: ConditionLifecycleSummary | null;
  scanLifecycle?: CanonicalReportLifecycle | null;
  historicalScanCount?: number | null;
  siteId: string;
  conditionId: string;
  areaId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  conditionFamily?: string | null;
  canOpenScan: boolean;
  timeLabel: (value: string | null | undefined) => string | null;
}): SpatialOperationsStageView[] {
  const lifecycle = args.lifecycle || null;
  const immediate = lifecycle?.latestImmediateIncident || null;
  const pattern = lifecycle?.latestTemporalPatternIncident || null;
  const reason = presentPatternAlertReason(pattern);
  const customerPattern = presentStoredPatternCustomerCopy(
    reason?.minEvents ?? pattern?.patternMinEvents,
    reason?.windowSeconds ?? pattern?.patternWindowSeconds,
  );
  const control = resolveCanonicalControl({ lifecycle });
  const scan = lifecycle?.latestScan || null;
  const postControl = presentPostControlPattern({
    sinceStartedAt: lifecycle?.sinceStartedAt,
    latestPattern: pattern,
    scan,
    backend: lifecycle?.postControlPattern,
  });
  const immediateTime = args.timeLabel(immediate?.occurredAt);
  const patternTime = args.timeLabel(pattern?.occurredAt);
  const controlTime = args.timeLabel(control.sinceStartedAt);
  const afterReportId = text(scan?.afterReportId) || null;
  const openHref = afterReportId
    ? buildLifecycleScanVerifyPath({
      siteId: args.siteId,
      conditionId: args.conditionId,
      areaId: args.areaId,
      zoneId: args.zoneId,
      cameraId: args.cameraId,
      reportId: afterReportId,
    })
    : null;

  const findDetail = [
    args.conditionLabel,
    args.zoneLabel || args.areaLabel,
    currentPostureCopy(args.current),
    recurrenceHistoryCopy(args.recurrenceRange),
    occurrenceLinkageCopy(args.linkedOccurrenceCount),
  ].filter(Boolean).join(' · ');
  const recurrenceValue = args.recurrenceRange?.value;

  const alertParts: string[] = [];
  if (immediate) {
    alertParts.push(`Immediate${immediate.severity ? ` · ${immediate.severity}` : ''}${immediateTime ? ` · ${immediateTime}` : ''}`);
  } else {
    alertParts.push('Immediate · none linked');
  }
  if (pattern) {
    alertParts.push(`Pattern · ${customerPattern || reason?.summary || 'linked'}${patternTime ? ` · ${patternTime}` : ''}`);
  } else {
    alertParts.push('Pattern · none linked');
  }

  const sinceState = control.authority === 'epic402' && control.sinceStartedAt
    ? (text(scan?.coverageState).toLowerCase() === 'insufficient' ? 'Coverage incomplete' : 'Active')
    : (control.authority === 'none' ? 'Not started' : 'Measurement pending');
  const sinceDetail = control.authority === 'none'
    ? 'Since requires a recorded Control implementation time.'
    : (control.sinceStartedAt
      ? `Measurement Since begins at the recorded implementation time${controlTime ? ` · ${controlTime}` : ''}. ${postControl.summary}`
      : 'Measurement pending. Implementation time is not linked.');

  let verifyState = 'No Safety Scan';
  let verifyDetail = 'No Safety Scan is linked for this condition.';
  if (!afterReportId && (args.historicalScanCount || 0) > 0) {
    verifyState = 'No Control-linked verification';
    verifyDetail = 'Historical Safety Scans available below.';
  }
  if (afterReportId) {
    if (args.scanLifecycle === 'Signed') {
      verifyState = 'Signed';
      verifyDetail = 'Safety Scan · Signed';
    } else if (args.scanLifecycle === 'Verified') {
      verifyState = 'Verified';
      verifyDetail = 'Safety Scan · Verified · Not signed';
    } else if (args.scanLifecycle === 'Draft') {
      verifyState = 'Draft';
      verifyDetail = 'Safety Scan · Draft';
    } else {
      verifyState = scan?.resultDirection ? 'Comparison linked' : 'Comparison pending';
      const linkedDirection = presentCustomerResultDirection(scan?.resultDirection);
      verifyDetail = linkedDirection
        ? `Linked Safety Scan · ${linkedDirection}`
        : 'Linked Safety Scan · comparison pending';
    }
    if (scan?.coverageState) verifyDetail += ` · Coverage: ${scan.coverageState}`;
    if (scan?.finality) verifyDetail += ` · ${scan.finality}`;
    const resultLabel = presentCustomerResultDirection(scan?.resultDirection);
    if (resultLabel && !verifyDetail.includes(resultLabel)) verifyDetail += ` · ${resultLabel}`;
  }

  return [
    {
      id: 'find',
      label: 'FIND',
      state: args.current?.measurementValue != null || text(args.current?.supportingActivityLabel)
        ? 'Observed'
        : (recurrenceValue == null
          ? 'Measurement pending'
          : (recurrenceValue > 0 ? 'Recurring history' : 'No recurrence recorded')),
      detail: findDetail,
      href: null,
    },
    {
      id: 'alerts',
      label: 'ALERTS',
      state: immediate || pattern ? (immediate && pattern ? 'Immediate and Pattern' : (immediate ? 'Immediate Alert' : 'Pattern Alert')) : 'None linked',
      detail: alertParts.join(' · '),
      href: null,
    },
    {
      id: 'control',
      label: 'CONTROL',
      state: control.authority === 'none' ? 'Not recorded' : 'Recorded',
      detail: control.authority === 'none'
        ? 'No Control recorded.'
        : `${control.label}${controlTime ? ` · ${controlTime}` : ''}. Measurement Since begins at the recorded implementation time.`,
      href: null,
    },
    {
      id: 'since',
      label: 'SINCE',
      state: sinceState,
      detail: sinceDetail,
      href: null,
    },
    {
      id: 'verify',
      label: 'VERIFY',
      state: verifyState,
      detail: verifyDetail,
      href: args.canOpenScan ? openHref : null,
    },
  ];
}

export function presentSpatialOperationsScan(args: {
  lifecycle?: ConditionLifecycleSummary | null;
  scanLifecycle?: CanonicalReportLifecycle | null;
  confirmedLabel?: string | null;
  usableHoursLabel?: string | null;
  siteId: string;
  conditionId: string;
  areaId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  conditionFamily?: string | null;
  canOpenScan: boolean;
}): SpatialOperationsScanView {
  const scan = args.lifecycle?.latestScan || null;
  const afterReportId = text(scan?.afterReportId) || null;
  const beforeReportId = text(scan?.beforeReportId) || null;
  const openHref = afterReportId
    ? buildLifecycleScanVerifyPath({
      siteId: args.siteId,
      conditionId: args.conditionId,
      areaId: args.areaId,
      zoneId: args.zoneId,
      cameraId: args.cameraId,
      reportId: afterReportId,
    })
    : null;
  return {
    linked: Boolean(afterReportId),
    reportId: afterReportId,
    beforeReportId: beforeReportId || null,
    afterReportId,
    lifecycle: afterReportId ? (args.scanLifecycle || null) : null,
    signed: args.scanLifecycle === 'Signed',
    coverageState: scan?.coverageState || null,
    finality: scan?.finality || null,
    resultDirection: scan?.resultDirection || null,
    resultDirectionLabel: presentCustomerResultDirection(scan?.resultDirection),
    confirmedLabel: args.confirmedLabel || null,
    usableHoursLabel: args.usableHoursLabel || null,
    openHref,
    canOpen: Boolean(args.canOpenScan && openHref),
    emptyCopy: 'No Safety Scan is linked for this condition.',
  };
}
